import { Injectable, computed, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { BillingService, SetupStatus, SetupStep } from './billing.service';
import { CoreService } from './core.service';

/** Menu items that open after a setup step (only for companies that signed up by themselves). */
const MENU_NEEDS: { prefix: string; needs: string[] }[] = [
  { prefix: 'apps/employee', needs: ['facility'] },
  { prefix: 'apps/applicant', needs: ['facility'] },
  { prefix: 'apps/punches', needs: ['facility'] },
  { prefix: 'apps/rentalVehicles', needs: ['facility'] },
  { prefix: 'apps/routes', needs: ['facility', 'zones'] },
  { prefix: 'apps/payroll', needs: ['facility', 'zones'] },
  { prefix: 'apps/loan', needs: ['facility', 'zones'] },
  // Pay advances se descuentan del payroll: igual que Payroll, espera la facility y las zonas.
  { prefix: 'apps/pay-advances', needs: ['facility', 'zones'] },
  { prefix: 'apps/bonus-approvals', needs: ['facility', 'zones'] },
  { prefix: 'apps/dailyRd', needs: ['facility', 'zones'] },
  { prefix: 'dashboards/pnl', needs: ['facility', 'zones'] }
];

/** Order of the step-by-step guide, with the element each step points at (data-tour="…" on that page). */
export const TOUR_STEPS: { key: string; target: string; text: string }[] = [
  { key: 'company', target: 'company-details', text: 'tour.company' },
  { key: 'facility', target: 'add-facility', text: 'tour.facility' },
  { key: 'location', target: 'checkin-locations', text: 'tour.location' },
  { key: 'zones', target: 'add-zone', text: 'tour.zones' },
  { key: 'drivers', target: 'join-link', text: 'tour.drivers' },
  { key: 'manager', target: 'applicants-list', text: 'tour.manager' },
  { key: 'branding', target: 'company-look', text: 'tour.branding' }
];

/** Where the arrow points for a step, by the page its link opens (Drivers: the join link, or Applicants to hire them). */
export function tourTarget(key: string, link: string | null | undefined): { target: string; text: string } {
  const path = (link || '').split('#')[0].split('?')[0];
  const base = TOUR_STEPS.find(t => t.key === key) ?? { target: '', text: '' };
  if (key === 'drivers' && path === '/apps/applicant') return { target: 'applicants-list', text: 'tour.driversHire' };
  return { target: base.target, text: base.text };
}

/**
 * The company's setup progress (GET Companies/setup-status), kept for the menu and the guide. Only companies that
 * signed up by themselves (billing status not empty) get the guide and the menu that opens step by step; companies from
 * before (TTO) see everything as always.
 */
@Injectable({ providedIn: 'root' })
export class SetupProgressService {
  private billing = inject(BillingService);
  private core = inject(CoreService);

  readonly status = signal<SetupStatus | null>(SetupProgressService.cached());

  /** Self signed-up company (billed through sign-up) whose owner or admin is logged in. */
  readonly managed = computed(() => {
    const s = this.status();
    return !!s && (s.billingStatus != null || s.selfServe === true);
  });

  readonly steps = computed<SetupStep[]>(() => this.status()?.steps ?? []);

  private static cached(): SetupStatus | null {
    try {
      const raw = localStorage.getItem('paq.setup');
      return raw ? JSON.parse(raw) as SetupStatus : null;
    } catch {
      return null;
    }
  }

  /** Owners and admins of a company load the checklist; nobody else needs it. */
  canLoad(): boolean {
    const role = this.core.getRole();
    return (role === 'CompanyOwner' || role === 'Admin') && this.core.getHasCompany();
  }

  load(): Observable<SetupStatus> {
    return this.billing.setupStatus().pipe(tap(s => {
      this.status.set(s);
      try { localStorage.setItem('paq.setup', JSON.stringify(s)); } catch { /* storage blocked */ }
    }));
  }

  refresh(): void {
    if (!this.canLoad()) return;
    this.load().subscribe({ error: () => { /* keep what we had */ } });
  }

  forget(): void {
    this.status.set(null);
    try { localStorage.removeItem('paq.setup'); } catch { /* storage blocked */ }
  }

  step(key: string): SetupStep | undefined {
    return this.steps().find(s => s.key === key);
  }

  isDone(key: string): boolean {
    return !!this.step(key)?.done;
  }

  /** The step a menu item waits for, or null when it is open. Companies from before: always open. */
  lockedBy(route: string | null | undefined): SetupStep | null {
    if (!route || !this.managed() || this.core.getRole() !== 'CompanyOwner') return null;
    const path = route.replace(/^\/+/, '');
    const rule = MENU_NEEDS.find(r => path.startsWith(r.prefix));
    if (!rule) return null;
    for (const key of rule.needs) {
      const s = this.step(key);
      if (s && !s.done) return s;
    }
    return null;
  }

  // ------------------------------------------------------------------ guide

  get guideOn(): boolean {
    try { return localStorage.getItem('paq.guide') === 'on'; } catch { return false; }
  }

  startGuide(): void {
    try { localStorage.setItem('paq.guide', 'on'); localStorage.removeItem('paq.guide.skip'); } catch { /* storage blocked */ }
  }

  stopGuide(): void {
    try { localStorage.removeItem('paq.guide'); } catch { /* storage blocked */ }
  }

  skipped(): string[] {
    try { return JSON.parse(localStorage.getItem('paq.guide.skip') || '[]'); } catch { return []; }
  }

  skip(key: string): void {
    const list = Array.from(new Set([...this.skipped(), key]));
    try { localStorage.setItem('paq.guide.skip', JSON.stringify(list)); } catch { /* storage blocked */ }
  }

  /** The guide's current step: the first one in the guide's order not done and not skipped. */
  currentGuideStep(): { tour: typeof TOUR_STEPS[number]; step: SetupStep; index: number; total: number } | null {
    const steps = this.steps();
    const skipped = this.skipped();
    const list = TOUR_STEPS.filter(t => steps.some(s => s.key === t.key));
    for (let i = 0; i < list.length; i++) {
      const step = steps.find(s => s.key === list[i].key)!;
      if (!step.done && !skipped.includes(step.key)) return { tour: list[i], step, index: i + 1, total: list.length };
    }
    return null;
  }

  /** All required steps are done. */
  allRequiredDone(): boolean {
    const req = this.steps().filter(s => !s.optional);
    return req.length > 0 && req.every(s => s.done);
  }
}
